import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Button, Col, DatePicker, Form, Row, Select, Space } from 'antd';
import { PlusOutlined, MinusCircleOutlined, DownOutlined, UpOutlined } from '@ant-design/icons';
import { FilterOperator } from '../../Types/FilterOperator';
import { TextService } from '../../Services/TextService';
import { BookyInput, InputType } from '../Forms/BookyInput';
import { WebApiModelsMemberField } from '../../Repository/eventbookyapi';
import { MemberFieldTypes } from '../../Types/MemberFieldTypes';
import { getFieldText, getFieldValueName } from '../../Helpers/MemberFieldHelper';
import { OneFrontDatePicker } from '../Forms/BookyDatePicker';
import { UiControlType } from '../../Types/UiControlType';
import { formatToShortFormat } from '../../Helpers/DateHelpers';
import dayjs from 'dayjs';

export interface FilterCriteria {
    fieldName: string;
    operator: FilterOperator;
    value?: string;
    logic?: 'And' | 'Or';
}

export interface FilterRequest {
    filters: FilterCriteria[];
    filterLogic: 'And' | 'Or';
}

interface AdvancedMemberFilterProps {
    memberFieldSettings: WebApiModelsMemberField[];
    onFilterChange: (filterRequest: FilterRequest) => void;
    hideApplyButton?: boolean;
}

export const AdvancedMemberFilter: React.FC<AdvancedMemberFilterProps> = ({
    memberFieldSettings,
    onFilterChange,
    hideApplyButton = false
}) => {
    const GetText = TextService.GetText;
    const [filters, setFilters] = useState<FilterCriteria[]>([]);
    const [filterLogic, setFilterLogic] = useState<'And' | 'Or'>('And');
    const [isExpanded, setIsExpanded] = useState(false);

    // When hideApplyButton is true, automatically notify parent of filter changes
    useEffect(() => {
        if (hideApplyButton) {
            onFilterChange({ filters, filterLogic });
        }
    }, [filters, filterLogic, hideApplyButton]);

    // Standard fields that are always available
    const standardFields = useMemo(() => [
        { label: GetText("MemberList_EmailColumn"), value: "Email", uicontrol: UiControlType.Text, type: MemberFieldTypes.Email },
        { label: GetText("MemberList_FirstNameColumn"), value: "FirstName", uicontrol: UiControlType.Text, type: MemberFieldTypes.FirstName },
        { label: GetText("MemberList_LastNameColumn"), value: "LastName", uicontrol: UiControlType.Text, type: MemberFieldTypes.LastName },
        { label: GetText("MemberList_PhoneNumberColumn"), value: "PhoneNumber", uicontrol: UiControlType.Text, type: MemberFieldTypes.PhoneNumber },
        { label: GetText("MemberList_UniqueIdColumn"), value: "UniqueId", uicontrol: UiControlType.Text, type: MemberFieldTypes.UniqueId },
        { label: GetText("MemberList_CompanyColumn"), value: "Company", uicontrol: UiControlType.Text, type: MemberFieldTypes.Company },
        { label: GetText("MemberList_AdressColumn"), value: "Address", uicontrol: UiControlType.Text, type: MemberFieldTypes.Address },
        { label: GetText("MemberList_PostalCodeColumn"), value: "PostalCode", uicontrol: UiControlType.Text, type: MemberFieldTypes.PostalCode },
        { label: GetText("MemberList_PostalAreaColumn"), value: "PostalArea", uicontrol: UiControlType.Text, type: MemberFieldTypes.PostalArea },
        { label: GetText("MemberList_CountryColumn"), value: "Country", uicontrol: UiControlType.Text, type: MemberFieldTypes.Country },
    ], []);

    // Combine standard fields with member field settings (system + custom fields)
    const allFields = useMemo(() => [
        ...standardFields,
        ...memberFieldSettings
            .map(field => {
                if (field.columnType === MemberFieldTypes.Custom) {
                    // For custom fields, use CustomField_{id} format
                    if (field.nameAdmin || field.namePublic) {
                        return {
                            type: field.columnType,
                            uicontrol: field.uiControl,
                            label: field.nameAdmin || field.namePublic || `Field ${field.id}`,
                            value: `CustomField_${field.id}`
                        };
                    }
                    return null;
                } else {
                    // For system fields that are NOT already in standardFields
                    const fieldValue = getFieldValueName(field.columnType!);
                    // Exclude Membership/Product field and fields already in standardFields
                    if (fieldValue &&
                        field.columnType !== MemberFieldTypes.Membership &&
                        !standardFields.find(sf => sf.value === fieldValue)) {
                        const fieldLabel = getFieldText(field.columnType!);
                        if (fieldLabel) {
                            return {
                                type: field.columnType,
                                uicontrol: field.uiControl,
                                label: fieldLabel,
                                value: fieldValue
                            };
                        }
                    }
                    return null;
                }
            })
            .filter(field => field !== null) as { label: string; value: string; type: MemberFieldTypes; uicontrol: UiControlType }[]
    ], [memberFieldSettings, standardFields]);

    const operatorOptions = [
        { label: GetText("Filter_Equals"), value: FilterOperator.Equals },
        { label: GetText("Filter_NotEquals"), value: FilterOperator.NotEquals },
        { label: GetText("Filter_Contains"), value: FilterOperator.Contains },
        { label: GetText("Filter_StartsWith"), value: FilterOperator.StartsWith },
        { label: GetText("Filter_EndsWith"), value: FilterOperator.EndsWith },
        { label: GetText("Filter_GreaterThan"), value: FilterOperator.GreaterThan },
        { label: GetText("Filter_LessThan"), value: FilterOperator.LessThan },
        { label: GetText("Filter_GreaterThanOrEqual"), value: FilterOperator.GreaterThanOrEqual },
        { label: GetText("Filter_LessThanOrEqual"), value: FilterOperator.LessThanOrEqual },
        { label: GetText("Filter_IsEmpty"), value: FilterOperator.IsEmpty },
        { label: GetText("Filter_IsNotEmpty"), value: FilterOperator.IsNotEmpty },
    ];

    const addFilter = () => {
        const newFilter: FilterCriteria = {
            fieldName: standardFields[0].value,
            operator: FilterOperator.Contains,
            value: '',
            logic: 'And'
        };
        const updatedFilters = [...filters, newFilter];
        setFilters(updatedFilters);
    };

    const removeFilter = (index: number) => {
        const updatedFilters = filters.filter((_, i) => i !== index);
        setFilters(updatedFilters);
        onFilterChange({ filters: updatedFilters, filterLogic });
    };

    const updateFilter = (index: number, field: keyof FilterCriteria, value: any) => {
        console.log("UPDATE FILTER" + value);
        const updatedFilters = [...filters];
        updatedFilters[index] = { ...updatedFilters[index], [field]: value };
        setFilters(updatedFilters);
    };

    const applyFilters = () => {
        onFilterChange({ filters, filterLogic });
    };

    const clearFilters = () => {
        setFilters([]);
        onFilterChange({ filters: [], filterLogic });
    };

    const needsValue = (operator: FilterOperator) => {
        return operator !== FilterOperator.IsEmpty && operator !== FilterOperator.IsNotEmpty;
    };

    return (
        <div style={{ marginBottom: '36px' }}>
            <div style={{ marginBottom: '12px' }}>
                <div
                    style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center' }}
                    onClick={() => setIsExpanded(!isExpanded)}
                >
                    <Space>
                        <span style={{ fontWeight: 500 }}>{GetText("Filter_AdvancedFilters")}</span>
                        <Button
                            type="link"
                            size="small"
                            icon={isExpanded ? <UpOutlined /> : <DownOutlined />}
                        />
                    </Space>
                </div>
            </div>
            {isExpanded && (
                <>
                    <Space style={{ marginBottom: '12px' }}>
                        <Button
                            type="dashed"
                            icon={<PlusOutlined />}
                            onClick={addFilter}
                        >
                            {GetText("Filter_AddFilter")}
                        </Button>
                        {!hideApplyButton && (
                            <Button
                                type="primary"
                                onClick={applyFilters}
                                disabled={filters.length === 0}
                            >
                                {GetText("Filter_ApplyFilters")}
                            </Button>
                        )}
                        <Button
                            onClick={clearFilters}
                            disabled={filters.length === 0}
                        >
                            {GetText("Filter_ClearFilters")}
                        </Button>
                    </Space>
                    <Space direction="vertical" style={{ width: '100%' }}>
                        {filters.map((filter, index) => (
                            <div key={index}>
                                <Row gutter={8} align="middle">
                                    <Col span={7}>
                                        <Select
                                            style={{ width: '100%' }}
                                            value={filter.fieldName}
                                            onChange={(value) => updateFilter(index, 'fieldName', value)}
                                            options={allFields}
                                            placeholder={GetText("Filter_SelectField")}
                                        />
                                    </Col>
                                    <Col span={5}>
                                        <Select
                                            style={{ width: '100%' }}
                                            value={filter.operator}
                                            onChange={(value) => updateFilter(index, 'operator', value)}
                                            options={operatorOptions}
                                            placeholder={GetText("Filter_SelectOperator")}
                                        />
                                    </Col>
                                    <Col span={8}>
                                        {needsValue(filter.operator) && (() => {
                                            const field = allFields.find(f => f.value === filter.fieldName);

                                            if (!field) return null;

                                            const isDate =
                                                field.uicontrol === UiControlType.Date ||
                                                field.type === MemberFieldTypes.JoinedDate;

                                            const isDropdown = field.uicontrol === UiControlType.Dropdown;

                                            if (isDate) {
                                                return <DatePicker format={"YYYY-MM-DD"} placeholder={GetText("Filter_SelectDate")} onChange={(date: any) => updateFilter(index, 'value', dayjs((date).toDate()).format("YYYY-MM-DD"))} />;
                                            }

                                            if (isDropdown) {
                                                const fieldSetting = memberFieldSettings.find(f => `CustomField_${f.id}` === filter.fieldName);
                                                const options = (fieldSetting?.alternatives ?? '').split(';').map(s => s.trim()).filter(Boolean).map(s => ({ label: s, value: s }));
                                                return (
                                                    <Select
                                                        style={{ width: '100%' }}
                                                        value={filter.value || undefined}
                                                        onChange={(value) => updateFilter(index, 'value', value)}
                                                        options={options}
                                                        placeholder={GetText("Filter_SelectValue")}
                                                        allowClear
                                                    />
                                                );
                                            }

                                            return (
                                                <input
                                                    type="text"
                                                    className="ant-input"
                                                    value={filter.value || ''}
                                                    onChange={(e) => updateFilter(index, 'value', e.target.value)}
                                                    placeholder={GetText("Filter_EnterValue")}
                                                />
                                            );
                                        })()}
                                    </Col>
                                    <Col span={1}>
                                        <Button
                                            type="text"
                                            danger
                                            icon={<MinusCircleOutlined />}
                                            onClick={() => removeFilter(index)}
                                        />
                                    </Col>
                                </Row>
                                {/* Show logic selector after each filter except the last one */}
                                {index < filters.length - 1 && (
                                    <Row style={{ marginTop: '8px', marginBottom: '8px' }}>
                                        <Col span={24} style={{ textAlign: 'center' }}>
                                            <Select
                                                style={{ width: '120px' }}
                                                value={filters[index + 1]?.logic || 'And'}
                                                onChange={(value) => updateFilter(index + 1, 'logic', value)}
                                                options={[
                                                    { value: 'And', label: GetText("Filter_And") },
                                                    { value: 'Or', label: GetText("Filter_Or") }
                                                ]}
                                            />
                                        </Col>
                                    </Row>
                                )}
                            </div>
                        ))}
                    </Space>
                </>
            )}
        </div>
    );
};

export default AdvancedMemberFilter;